/**
 * 3-tier CSV validation: format, columns, row data.
 * Stops at 10 row errors; returns structured ValidationResult.
 */

import {
  PayslipEventRowSchema,
  REQUIRED_COLUMNS,
  rowToRecord,
} from "./csvSchema";
import { parseCsvText } from "./csvParser";
import {
  createValidationError,
  createValidationResult,
  type ValidationResult,
} from "./types";

const MAX_ROW_ERRORS = 10;

/**
 * Validate CSV file content (async to read file text).
 * Check 1: Valid CSV format.
 * Check 2: All required columns present.
 * Check 3: Row-by-row schema validation, stop at 10 errors.
 */
export async function validatePayslipCsv(
  file: File,
): Promise<ValidationResult> {
  const text = await readFileAsText(file);
  return validatePayslipCsvText(text);
}

/**
 * Validate CSV text (sync, for testing or when text is already available).
 */
export function validatePayslipCsvText(text: string): ValidationResult {
  const parseResult = parseCsvText(text);
  if (!parseResult.ok) {
    return createValidationResult(
      "invalid",
      [createValidationError("format", 0, "", parseResult.message)],
      0,
    );
  }

  const { headers, rows } = parseResult;
  const columnErrors = validateColumns(headers);
  if (columnErrors.length > 0) {
    return createValidationResult("invalid", columnErrors, rows.length);
  }

  const rowErrors = validateRows(rows, headers);
  const status = rowErrors.length === 0 ? "valid" : "invalid";
  return createValidationResult(status, rowErrors, rows.length);
}

function validateColumns(
  headers: string[],
): ReturnType<typeof createValidationError>[] {
  const errors: ReturnType<typeof createValidationError>[] = [];
  const headerSet = new Set(headers.map((h) => h.trim()));

  for (const required of REQUIRED_COLUMNS) {
    if (!headerSet.has(required)) {
      errors.push(
        createValidationError(
          "columns",
          0,
          required,
          `Missing required column: ${required}`,
        ),
      );
    }
  }

  const extra = headers.filter((h) => !REQUIRED_COLUMNS.includes(h.trim()));
  if (extra.length > 0) {
    errors.push(
      createValidationError(
        "columns",
        0,
        "",
        `Unexpected column(s): ${extra.join(", ")}. Expected exactly: ${REQUIRED_COLUMNS.join(", ")}.`,
      ),
    );
  }

  return errors;
}

function validateRows(
  rows: string[][],
  headers: string[],
): ReturnType<typeof createValidationError>[] {
  const errors: ReturnType<typeof createValidationError>[] = [];

  for (let i = 0; i < rows.length; i++) {
    if (errors.length >= MAX_ROW_ERRORS) break;

    const rowIndex = i + 2;
    const cells = rows[i];
    const record = rowToRecord(headers, cells);
    const parsed = PayslipEventRowSchema.safeParse(record);

    if (parsed.success) continue;

    const issues = parsed.error.issues;
    for (const issue of issues) {
      if (errors.length >= MAX_ROW_ERRORS) break;
      const path = issue.path[0];
      const column = typeof path === "string" ? path : "";
      errors.push(
        createValidationError("row", rowIndex, column, issue.message),
      );
    }
  }

  return errors;
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string) ?? "");
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file, "UTF-8");
  });
}
